import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  #randomGeneration = 0;
  #randomReady = false;
  #randomKey = '';
  #randomFailedKey = '';
  #randomMode = false;
  #randomPromise;
  #randomApplying = false;
  #randomObserver = new MutationObserver(() => {
    if (this.#randomApplying || !this.isConnected) return;
    if (!this.hasAttribute('data-lunera-multi-random') && !this.#randomMode) return;
    if (this.#randomFailedKey === this.#randomSignature()) return;
    if (this.#randomKey === this.#randomSignature() && (this.#randomReady || this.#randomPromise)) return;
    super.updatedCallback();
    this.#startPagination();
  });

  #useRandomOrder() {
    if (!this.hasAttribute('data-lunera-multi-random')) return false;
    const sort = new URL(window.location.href).searchParams.get('sort_by');
    return !sort || sort === 'manual' || sort === 'most-relevant';
  }

  #randomSignature() {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('page');
    url.searchParams.delete('grid');
    url.searchParams.sort();
    return url.href;
  }

  #startPagination() {
    if (!this.#useRandomOrder()) {
      this.#randomMode = false;
      this.#randomReady = false;
      this.#randomKey = '';
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
      return;
    }
    this.#randomMode = true;
    const key = this.#randomSignature();
    if (this.#randomFailedKey === key) return;
    if (this.#randomKey === key && (this.#randomReady || this.#randomPromise)) return;
    this.infinityScrollObserver?.disconnect();
    this.#randomKey = key;
    this.#randomReady = false;
    const generation = ++this.#randomGeneration;
    const pending = this.#prepareRandomPages(generation).catch(() => {
      if (generation !== this.#randomGeneration || !this.isConnected) return;
      this.#randomFailedKey = key;
      this.pages.clear();
      this.dataset.luneraRandomState = 'fallback';
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }).finally(() => {
      if (generation === this.#randomGeneration) this.#randomPromise = undefined;
    });
    this.#randomPromise = pending;
  }

  async #prepareRandomPages(generation) {
    const grid = this.refs.grid;
    if (!grid) return;
    const lastPage = Number(grid.dataset.lastPage) || 1;
    const pageSize = Number(this.getAttribute('data-lunera-page-size')) || 24;
    const expected = Number(this.getAttribute('data-lunera-result-count'));
    const currentURL = new URL(window.location.href);
    const currentPage = Number(currentURL.searchParams.get('page')) || 1;
    const pool = new Map();
    const addCards = source => {
      for (const card of source.querySelectorAll(':scope > [ref="cards[]"]')) {
        if (card.dataset.productId) pool.set(card.dataset.productId, card.cloneNode(true));
      }
    };
    addCards(grid);
    this.dataset.luneraRandomState = 'loading';
    grid.setAttribute('aria-busy', 'true');
    const remaining = Array.from({ length: lastPage }, (_, i) => i + 1).filter(page => page !== currentPage);
    const worker = async () => {
      while (remaining.length && generation === this.#randomGeneration && this.isConnected) {
        const page = remaining.shift();
        const url = new URL(currentURL);
        url.hash = '';
        url.searchParams.set('page', String(page));
        const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('[ref="grid"]');
        if (!source) throw new Error('Missing collection grid');
        addCards(source);
      }
    };
    try {
      await Promise.all([worker(), worker()]);
      if (generation !== this.#randomGeneration || !this.isConnected || this.refs.grid !== grid) return;
      if (expected && pool.size !== expected) throw new Error('Incomplete filtered product pool');
      let seed;
      try { seed = sessionStorage.getItem('lunera-multi-brand-seed'); } catch {}
      if (!seed) {
        seed = String(crypto.getRandomValues(new Uint32Array(1))[0]);
        try { sessionStorage.setItem('lunera-multi-brand-seed', seed); } catch {}
      }
      const rank = id => {
        let value = 2166136261;
        for (const char of seed + ':' + id) {
          value ^= char.charCodeAt(0);
          value = Math.imul(value, 16777619);
        }
        value ^= value >>> 16;
        value = Math.imul(value, 0x7feb352d);
        value ^= value >>> 15;
        value = Math.imul(value, 0x846ca68b);
        return (value ^ (value >>> 16)) >>> 0;
      };
      const cards = [...pool.values()].sort((a, b) => rank(a.dataset.productId) - rank(b.dataset.productId) || a.dataset.productId.localeCompare(b.dataset.productId));
      const pages = new Map();
      for (let page = 1; page <= lastPage; page++) {
        const shell = grid.cloneNode(false);
        shell.removeAttribute('aria-busy');
        const group = cards.slice((page - 1) * pageSize, page * pageSize);
        for (const card of group) card.dataset.page = String(page);
        shell.append(...group);
        pages.set(page, shell.outerHTML);
      }
      this.pages = pages;
      this.#randomReady = true;
      this.#randomApplying = true;
      const initial = new DOMParser().parseFromString(pages.get(currentPage), 'text/html').querySelector('[ref="grid"]');
      grid.replaceChildren(...initial.children);
      super.updatedCallback();
      this.#randomApplying = false;
      this.dataset.luneraRandomState = 'ready';
      this.dataset.luneraRandomTotal = String(cards.length);
      this.#aspectRatioHelper?.processNewElements();
      this.#observeViewMore();
    } finally {
      if (generation === this.#randomGeneration) grid.removeAttribute('aria-busy');
    }
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.#randomApplying && (this.hasAttribute('data-lunera-multi-random') || this.#randomMode)) this.#startPagination();
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#randomObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-lunera-result-count', 'data-lunera-multi-random'] });
    this.#startPagination();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    this.#randomGeneration++;
    this.#randomPromise = undefined;
    this.#randomObserver.disconnect();
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (this.#randomReady && this.pages.has(pageNumber)) return;
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const generation = this.#randomGeneration;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    if (generation === this.#randomGeneration && !this.#randomReady) this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#randomGeneration++;
    this.#randomReady = false;
    this.#randomPromise = undefined;
    this.#randomKey = '';
    this.#randomFailedKey = '';
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    if (this.hasAttribute('data-lunera-multi-random')) {
      this.infinityScrollObserver?.disconnect();
      return;
    }

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}

